import { DatePipe } from '@angular/common';
import { ChangeDetectorRef, Component, computed, inject, Input, OnInit, Signal, signal, WritableSignal } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Observable } from 'rxjs';
import { debounceTime, map, startWith, takeUntil } from 'rxjs/operators';
import { AddContactComponent } from 'src/app/AddThings/add-contact/add-contact.component';
import { AddLoanComponent } from 'src/app/AddThings/add-loan/add-loan.component';
import { AddCustomerBankAccountComponent } from 'src/app/BankAccount/add-customer-bank-account/add-customer-bank-account.component';
import { BasicDialogComponent } from 'src/app/BaseDialog/basic-dialog/basic-dialog.component';
import { GenerateFormjComponent } from 'src/app/FormJ/generate-formj/generate-formj.component';
import { UploadImageComponent } from 'src/app/Images/upload-image/upload-image.component';
import { ElevateConfirmationComponent } from 'src/app/LoanRequest/elevate-confirmation/elevate-confirmation.component';
import { LoanRequestDetailsComponent } from 'src/app/LoanRequest/loan-request-details/loan-request-details.component';
import { LoanRequestMessageComponent } from 'src/app/MessageClipboard/loan-request-message/loan-request-message.component';
import { Enums } from 'src/app/Shared/enums';
import { SharedService } from 'src/app/Shared/shared-service.service';
import { ALERT_TYPE, ERROR_MESSAGES, FORMJ_SCHEME, LOAN_ERROR_MESSAGES } from 'src/app/constants';
import { DialogItem } from 'src/app/dialog-item';
import { Company } from 'src/model/company.model';
import { Contact } from 'src/model/contact.model';
import { Customer } from 'src/model/customer.model';
import { EPF } from 'src/model/epf.model';
import { FormJ } from 'src/model/formJ.model';
import { Loan } from 'src/model/loan.model';
import { LoanRequest } from 'src/model/loanRequest.model';
import { Profile } from 'src/model/profile.model';
import { SettlementCharges } from 'src/model/settlementCharges.model';
import { SharedLoanRequest } from 'src/model/sharedLoan.model';
import { ContactService } from 'src/services/contact.service';
import { CustomerService } from 'src/services/customer.service';
import { EpfService } from 'src/services/epf.service';
import { FormJService } from 'src/services/form-j.service';
import { LoanRequestService } from 'src/services/loan-request.service';

interface AddedLoanRequest {
  companyId: number,
  companyName: string,
  request: LoanRequest
}

@Component({
  selector: 'app-shared-loan',
  templateUrl: './shared-loan.component.html',
  styleUrls: ['./shared-loan.component.scss'],
  providers: [DatePipe],
  standalone: false
})
export class SharedLoanComponent implements OnInit {
  private activeModal = inject(NgbActiveModal);
  protected sharedService = inject(SharedService);
  protected enums = inject(Enums);
  private cdr = inject(ChangeDetectorRef);
  private _formBuilder = inject(UntypedFormBuilder);
  private router = inject(Router);
  private epfService = inject(EpfService);
  private customerService = inject(CustomerService);
  private loanRequestService = inject(LoanRequestService);
  private contactService = inject(ContactService);
  private formJService = inject(FormJService);

  requestList: AddedLoanRequest[] = [];
  // isEPF = true

  @Input() selectedCustomer: Customer;
  @Input() selectedProfile: Profile;
  @Input() epfDetails: EPF;
  @Input() isEPFExtra = false;
  @Input() previousEPFLoans: Loan[] = [];

  updatingCustomer: Customer = new Customer();
  updatingEPF: EPF = new EPF();

  handlingChargeSignal: WritableSignal<boolean> = signal(true);
  epfErrors: WritableSignal<string[]> = signal(undefined);
  invalidEPF: Signal<boolean> = computed(() => this.epfErrors() ? this.epfErrors().length > 0 : true);
  guarantorErrors: WritableSignal<string> = signal(undefined);
  invalidGuarantor: Signal<boolean> = computed(() => this.guarantorErrors() === undefined ? true : this.guarantorErrors().length > 0);

  // epfFormGroup: UntypedFormGroup;
  epfFormGroup = this._formBuilder.group({
    Date: [undefined, Validators.required],
    Amount: [0, Validators.required],
    HasIAccount: [false],
    IAccountUsername: [''],
    IAccountPassword: [''],
    OtherId: [''],
    Gender: ['', Validators.required],
    Race: ['', Validators.required]
  });

  previousEPFFormData: any;

  mainLoanRequest: AddedLoanRequest;

  calcEPFDate: Date;

  isManualElevate = false;
  isShared = false;
  isManualKeyIn = false;

  hasEPFStatement = false;
  hasEPFProof = false;
  hasCustomerAcknowledgement = false;

  contactList: Contact[] = [];
  formJList: FormJ[] = [];

  loggedInCompanyName: string;
  loggedInCompanyId: number;

  epfDetailsExpansionIsOpen = true;
  guarantorExpansionIsOpen = true;
  formJExpansionIsOpen = true;

  disabledSubmit = false;

  elevateError = '';
  elevateLoanError = '';

  companyList: Company[] = [];
  selectedCompany: number;
  selectedCompanyCode: string;

  selectedFormJ: number = undefined;

  myControl = new UntypedFormControl();
  filteredCompany: Observable<Company[]>;

  readonly contactFiles: string[] = [this.enums.CONTACT_GUARANTOR_FORM, this.enums.CONTACT_PROOF_OF_RELATION, this.enums.CONTACT_ACKNOWLEDGEMENT];
  contactFileExistence: Record<number, string[]>;

  combinedLoan: Loan;

  constructor() {
    //filter company
    this.filteredCompany = this.myControl.valueChanges.pipe(
      startWith(''),
      map(companyCode => this._filterCompanyCodeOptions(companyCode))
    );

    this.epfFormGroup.valueChanges.pipe(debounceTime(300)).subscribe(data => {
      if (this.previousEPFFormData) {
        Object.keys(this.epfFormGroup.value).forEach(key => {
          if (JSON.stringify(data[key]) !== JSON.stringify(this.previousEPFFormData[key])) {
            if (key in this.updatingEPF)
              this.updatingEPF[key] = this.epfFormGroup.value[key];
            else if (key in this.updatingCustomer)
              this.updatingCustomer[key] = this.epfFormGroup.value[key];
          }
        })
        this.previousEPFFormData = data;
      }

      this.isEPFValid(false, false);
    })
  }

  async ngOnInit() {
    this.loggedInCompanyName = sessionStorage.getItem('companyName');
    this.loggedInCompanyId = Number(sessionStorage.getItem('companyId'));

    //check if is a previously shared customer, default make the loan to be a shared loan
    if (this.selectedProfile !== undefined && this.selectedProfile.SharedProfileId !== undefined) {
      this.isShared = true;
      this.isManualKeyIn = true;
    }
    if (this.selectedCustomer.DOB)
      this.calcEPFDate = this.sharedService.calculateEPFDate(this.selectedCustomer.DOB);
    await Promise.all([this.getEPFDetails(), this.checkEPFFileExistence(), this.getContactInfo(), this.getFormJs()]);
    this.updatingEPF = Object.assign(new EPF(), this.epfDetails);
    this.updatingEPF.Date ||= this.calcEPFDate;
    this.updatingCustomer = Object.assign(new Customer(), this.selectedCustomer);
    this.bindFormGroupInfo();

    this.isEPFValid(false);
  }

  // #region 'EPF'
  async getEPFDetails() {
    if (this.epfDetails.Date === undefined && this.selectedCustomer.EPFDate !== undefined) {
      this.epfDetails = await this.epfService.getCustomerEPF(this.selectedCustomer.ID);
    }
  }

  isEPFValid(promptError = true, toggleExpansion = true) {
    this.epfErrors.set([
      ...this.validateEpfDetails(this.updatingEPF),
      ...this.validateCustomerDetails(this.updatingCustomer),
      ...this.checkEPFAmountLimit(this.updatingEPF)
    ]);
    if (toggleExpansion) setTimeout(() => this.epfDetailsExpansionIsOpen = this.invalidEPF());
    if (promptError && this.invalidEPF()) this.showErrors(this.formatErrors(this.epfErrors()));
  }

  validateCustomerDetails(customer: Customer): string[] {
    const errors = [];
    if (!customer.Gender)
      errors.push("Missing Gender");
    if (!customer.Race)
      errors.push("Missing Race");
    return errors;
  }

  async checkEPFFileExistence() {
    this.hasEPFStatement = await this.epfService.getCompanyCustomerEPFStatementExistence(this.loggedInCompanyId, this.selectedCustomer.ID);
    if (this.isEPFExtra == false) {
      [this.hasEPFProof, this.hasCustomerAcknowledgement] = await Promise.all([
        this.customerService.getCustomerTypedFileExistence(this.selectedCustomer.ID, this.enums.EPF_PROOF),
        this.customerService.getCustomerTypedFileExistence(this.selectedCustomer.ID, this.enums.ACKNOWLEDGEMENT)
      ]);
    }
  }

  openUploadEPFSupportingDoc(supportingDocType: string) {
    const modalRef = this.sharedService.openModal(UploadImageComponent);
    modalRef.componentInstance.selectedCustomerId = this.selectedCustomer.ID;
    modalRef.componentInstance.supportingDocType = supportingDocType;
    //get added loan
    modalRef.componentInstance.passEntry.pipe(
      takeUntil(modalRef.closed),
      takeUntil(modalRef.dismissed)
    ).subscribe((entry) => {
      if (entry != undefined) {
        switch (supportingDocType) {
          case this.enums.EPF_STATEMENT:
            this.hasEPFStatement = true;
            break;
          case this.enums.EPF_PROOF:
            this.hasEPFProof = true;
            break;
          case this.enums.ACKNOWLEDGEMENT:
            this.hasCustomerAcknowledgement = true;
            break;
        }
      }
    });

    modalRef.result.then(() => {
      //detect changes
      this.cdr.detectChanges();
    })
  }

  validateEpfDetails(epf: EPF): string[] {
    const errors = [];
    if (epf.Date === undefined)
      errors.push("Missing EPF Date");
    else if (epf.Date <= new Date())
      errors.push("Customer's EPF Date is over");
    if (!epf.Amount)
      errors.push("Missing EPF Amount");
    if (!this.hasEPFStatement)
      errors.push("Please upload EPF Statement");
    if (!this.hasEPFProof && this.isEPFExtra === false)
      errors.push("Please upload EPF Proof");
    if (!this.hasCustomerAcknowledgement && this.isEPFExtra === false)
      errors.push("Please upload Customer Acknowledgement");
    return errors;
  }

  checkEPFAmountLimit(epf: EPF): string[] {
    //only restrict to NEW customer: NEW CUSTOMER that epf amount which is less than MINIMUM EPF AMOUNT of each category should be stopped
    // epf amount limit category can be found in enums
    const errors = [];
    if (this.isEPFExtra == false) {
      const category = this.sharedService.checkEPFAmountLimitCategory(epf.Date);
      if (epf.Amount < this.enums.epfAmountCategory[category].MinimumEPFAmount) {
        const error = `TERM ${this.enums.epfAmountCategory[category].LowerLimit} - ${this.enums.epfAmountCategory[category].UpperLimit} months AND EPF Amount less than RM ${this.sharedService.roundTo2Dec(this.enums.epfAmountCategory[category].MinimumEPFAmount)} is not allowed`;
        if (this.isManualElevate)
          this.elevateError = error;
        else
          errors.push(error);
      }
    }
    return errors;
  }

  get isValidEPFDate() {
    if (this.updatingEPF?.Date !== undefined && this.calcEPFDate !== undefined)
      return this.sharedService.isSameDate(this.updatingEPF.Date, this.calcEPFDate);
  }

  get EPFDate() {
    return this.epfDetails.Date ?? this.updatingEPF.Date;
  }

  get EPFAmount() {
    return this.epfDetails.Amount ?? this.updatingEPF.Amount;
  }

  async updateEPF() {
    this.epfDetails = this.epfDetails.Date !== undefined ? await this.epfService.updateCustomerEPF(this.selectedCustomer.ID, this.updatingEPF.toUpdate()) : await this.epfService.postCustomerEPF(this.selectedCustomer.ID, this.updatingEPF);
  }

  checkIfEPFDateOver(): boolean {
    if (this.EPFDate !== undefined)
      return this.EPFDate <= new Date();
    else
      return false;
  }
  // #endregion

  // #region 'Guarantor'
  async getContactInfo() {
    this.contactList = await this.contactService.getAllCustomerContactsWithPhotoExistence(this.selectedCustomer.ID);
    const results = await Promise.all(this.contactList.map(c => Promise.all(
      this.contactFiles.map(f => this.contactService.getCustomerContactTypedFileExistence(this.selectedCustomer.ID, c.ID, f))
    )));
    this.contactFileExistence = {};
    this.contactList?.forEach((c, index) => {
      this.contactFileExistence[c.ID] = this.contactFiles.filter((_, i) => results[index][i]);
    });
  }

  addContact() {
    const modalRef = this.sharedService.openModal(AddContactComponent);
    modalRef.componentInstance.viewCustomerId = this.selectedCustomer.ID;

    modalRef.componentInstance.passEntry.pipe(
      takeUntil(modalRef.closed),
      takeUntil(modalRef.dismissed)
    ).subscribe((entry) => {
      const contact = entry;
      this.contactList.push(contact);
      this.cdr.detectChanges();
    });
  }

  openContactUploadFileDialog(contact: Contact, docType: string, index: number) {
    const modalRef = this.sharedService.openModal(UploadImageComponent);
    modalRef.componentInstance.selectedCustomerId = this.selectedCustomer.ID;
    modalRef.componentInstance.contactId = contact.ID;
    modalRef.componentInstance.contactDocType = docType;
    modalRef.componentInstance.passEntry.pipe(
      takeUntil(modalRef.closed),
      takeUntil(modalRef.dismissed)
    ).subscribe(() => {
      if (docType === this.enums.CONTACT_PHOTO)
        this.contactList[index].PhotoExisted = true;
      else if (!(contact.ID in this.contactFileExistence))
        this.contactFileExistence[contact.ID] = [docType];
      else if (!this.contactFileExistence[contact.ID].includes(docType))
        this.contactFileExistence[contact.ID].push(docType);
    });
  }

  toggleContactSelected(i: number) {
    this.contactList[i].IsSelected = !this.contactList[i].IsSelected;
    this.isGuarantorValid(false, false);
  }

  isGuarantorValid(promptError = true, toggleExpansion = true) {
    if (this.contactList == undefined || this.contactList.length == 0 || !this.contactList.some(c => c.IsSelected))
      this.guarantorErrors.set('Please at least add a guarantor!');
    else if (this.contactList.some(c => c.IsSelected && c.PhotoExisted !== true))
      this.guarantorErrors.set('Please make sure all your guarantors contains picture!');
    else this.guarantorErrors.set('');
    if (toggleExpansion) setTimeout(() => this.guarantorExpansionIsOpen = this.invalidGuarantor());
    if (promptError && this.invalidGuarantor())
      this.sharedService.openAlert(this.guarantorErrors(), ALERT_TYPE.DANGER);
  }
  // #endregion

  // #region 'Form J'
  async getFormJs() {
    this.formJList = await this.formJService.getCustomerFormJs(this.selectedCustomer.ID, { scheme: FORMJ_SCHEME.EPF });
    const today = new Date();
    const oneMonthBefore = new Date();
    oneMonthBefore.setMonth(today.getMonth() - 1);
    this.formJList = this.formJList.filter(f => f.DateCreated > oneMonthBefore && f.DateCreated <= today);
  }

  selectFormJ(event: Event, id: number) {
    const checked = (event.target as HTMLInputElement).checked;
    this.selectedFormJ = checked ? id : undefined;
  }

  isFormJSelected() {
    const selected = this.selectedFormJ !== undefined
    setTimeout(() => this.formJExpansionIsOpen = !selected);
    if (!selected)
      this.sharedService.openAlert('Please select a Form J!', ALERT_TYPE.DANGER);
  }

  addFormJ() {
    const modalRef = this.sharedService.openModal(GenerateFormjComponent, "regularModal");
    modalRef.componentInstance.viewCustomerId = this.selectedCustomer.ID;
    modalRef.componentInstance.isEPF = true;
    modalRef.componentInstance.redirect = false;
    modalRef.componentInstance.passEntry.subscribe((formJ: FormJ) => {
      this.formJList.push(formJ);
      this.selectedFormJ = formJ.Id;
    });
    modalRef.result.then(() => {
      //detect changes
      this.cdr.detectChanges();
    })
  }
  // #endregion

  // #region 'Company'
  private _filterCompanyCodeOptions(value: string): Company[] {
    const filterValue = value.toLowerCase();
    const filtered = this.companyList.filter(company => { return company.CompanyCode.toLowerCase().includes(filterValue) });

    return filtered;
  }

  getSelectedCompany(company: Company, requestIndex: number) {
    if (this.loggedInCompanyId != company.ID) {
      this.requestList[requestIndex].companyId = company.ID;
      this.requestList[requestIndex].companyName = company.CompanyCode;

      this.cdr.detectChanges();
    }
    else {
      this.sharedService.openAlert("Cannot open another loan for main company in same request", ALERT_TYPE.DANGER);
      this.deleteNewLoan(requestIndex)
    }
  }
  // #endregion

  bindFormGroupInfo() {
    this.epfFormGroup.patchValue({
      Date: this.updatingEPF.Date,
      Amount: this.epfDetails.Amount,
      HasIAccount: this.epfDetails.HasIAccount,
      IAccountUsername: this.epfDetails.IAccountUsername,
      IAccountPassword: this.epfDetails.IAccountPassword,
      Remark: this.epfDetails.Remark,
      OtherId: this.selectedCustomer.OtherID,
      Gender: this.selectedCustomer.Gender,
      Race: this.selectedCustomer.Race
    })
    this.previousEPFFormData = this.epfFormGroup.getRawValue();
  }

  reset() {
    this.activeModal.close()
  }

  openLoanDetailsModal(request: LoanRequest) {
    const modalRef = this.sharedService.openModal(LoanRequestDetailsComponent, "regularModal");
    modalRef.componentInstance.isModal = true;
    modalRef.componentInstance.request = request;
  }

  openAddLoanModal(shareLoanRequestIndex?: number) {
    const modalRef = this.sharedService.openModal(AddLoanComponent, "regularModal");
    modalRef.componentInstance.selectedCustomer = this.selectedCustomer;
    modalRef.componentInstance.selectedProfile = this.selectedProfile;

    if (this.epfDetails.Date != undefined)
      modalRef.componentInstance.epfDetails = this.epfDetails;
    else {
      modalRef.componentInstance.epfDetails = this.updatingEPF ?? new EPF();
    }

    modalRef.componentInstance.isEPF = true;
    modalRef.componentInstance.isEPFExtra = this.isEPFExtra;

    modalRef.componentInstance.needRestrictedRequest = this.sharedService.checkIsSeniorRestrictedCustomer(new Date(this.selectedCustomer.DOB));
    // modalRef.componentInstance.allowEPF = this.sharedService.checkIsAllowEPF(this.selectedCustomer.DOB, this.epfDetails, this.selectedProfile.ID, this.selectedProfile.SharedProfileId);

    modalRef.componentInstance.isManualElevate = this.isManualElevate;
    modalRef.componentInstance.isManualKeyIn = this.isManualKeyIn;

    //if this is not logged in user's company loan
    if (shareLoanRequestIndex != undefined && this.requestList[shareLoanRequestIndex].companyId != this.loggedInCompanyId)
      modalRef.componentInstance.oboCompanyId = this.requestList[shareLoanRequestIndex].companyId;

    //get added loan
    modalRef.componentInstance.passEntry.pipe(
      takeUntil(modalRef.closed),
      takeUntil(modalRef.dismissed)
    ).subscribe((entry) => {
      const request: LoanRequest = entry;
      request.Guarantors = this.contactList.filter(c => c.IsSelected);

      if (shareLoanRequestIndex == undefined) {
        const loanRequest = { request: request, companyId: this.loggedInCompanyId, companyName: this.loggedInCompanyName }
        this.mainLoanRequest = loanRequest;
        // this.requestList.push(loanRequest);
      }

      else {
        // this.requestList[shareLoanRequestIndex].companyId = this.selectedCompany;
        // this.requestList[shareLoanRequestIndex].companyName = this.selectedCompanyCode;
        this.requestList[shareLoanRequestIndex].request = request;
      }

      this.checkHandlingChargeEligibility();
      this.cdr.detectChanges();
    });

    // modalRef.result.then(() => {
    //   // detect changes
    //   this.cdr.reattach();
    //   // console.log(this.requestList);
    // })
  }

  checkHandlingChargeEligibility() {
    if (this.isShared && this.requestList.length > 0) {
      const mainPrincipal = this.mainLoanRequest.request.LoanInfo.Principal;
      const totalPrincipal = this.requestList.reduce((total, current) => total + current.request.LoanInfo.Principal, mainPrincipal);
      const principalList = [mainPrincipal, ...this.requestList.map(r => r.request.LoanInfo.Principal)];
      this.handlingChargeSignal.update(v => v = !(principalList.some(p => p / totalPrincipal > this.enums.MINIMUM_SHARED_LOAN_PORTION)));
    } else this.handlingChargeSignal.update(v => v = false);
  }

  addHandlingChargeToExtra(settlementCharges: SettlementCharges) {
    settlementCharges.Extras = (settlementCharges.Extras ?? 0) + (settlementCharges.HandlingCharge ?? 0);
    settlementCharges.HandlingCharge = undefined;
  }

  async submitLoanRequest() {
    this.combinedLoan = this.combineAllLoans();
    let errors: string = this.formatErrors(this.validateLoan(this.combinedLoan));
    const limitErrors: string = this.formatErrors(this.validateLoanLimit(this.combinedLoan), true);
    if (this.isManualElevate === true)
      this.elevateLoanError = `- ${this.elevateError}\n\n${limitErrors}`;
    else
      errors = [errors, limitErrors].filter(e => e !== undefined && e.trim().length > 0).join('\n');
    if (errors?.length > 0) {
      this.showErrors(errors);
    } else {
      const mainRequest = this.sharedService.deepClone(this.mainLoanRequest.request);
      const sharedRequests = this.requestList.map(r => this.sharedService.deepClone(r.request));

      // If not eligible to get handling charge, add into duit lebih
      if (!this.handlingChargeSignal()) {
        this.addHandlingChargeToExtra(mainRequest.LoanInfo.SettlementCharges);
        sharedRequests.map(r => this.addHandlingChargeToExtra(r.LoanInfo.SettlementCharges));
      }

      const elevate = this.isManualElevate && (await this.confirmBeforeElevatingCase() === true);
      if (elevate === this.isManualElevate) {
        this.disabledSubmit = true;
        try {
          const promises: Promise<any>[] = [];
          if (!this.epfDetails.equals(this.updatingEPF) && this.sharedService.isBranchAccountHandler())
            promises.push(this.updateEPF());

          if (this.selectedCustomer.Gender.length === 0 || this.selectedCustomer.Race.length === 0 || this.selectedCustomer.OtherID !== this.updatingCustomer.OtherID)
            promises.push(this.updateCustomerDetails());

          if (promises.length > 0)
            await Promise.all(promises);

          this.isShared ? this.postSharedLoanRequest(mainRequest, sharedRequests, elevate) : this.postLoanRequest(mainRequest, elevate);
        } finally {
          this.disabledSubmit = false;
        }
      }
    }
  }

  async postLoanRequest(loanRequest: LoanRequest, elevate: boolean) {
    loanRequest.Remark += (elevate == true) ? "=\n" + this.elevateLoanError : '';
    try {
      const r = await this.loanRequestService.postLoanRequest(loanRequest, this.selectedFormJ, elevate);
      if (this.sharedService.isBranchAccountHandler()) {
        this.mainLoanRequest.request.ID = r.ID;
        this.mainLoanRequest.request.Remark = r.Remark;
        this.mainLoanRequest.request.DOB = this.selectedCustomer.DOB;
        this.mainLoanRequest.request.OtherId = this.selectedCustomer.OtherID;
        const modalRef = this.sharedService.openModal(LoanRequestMessageComponent);
        modalRef.componentInstance.requestList = [this.mainLoanRequest.request];
        modalRef.componentInstance.isDirty = true;
        modalRef.componentInstance.isEPFRequest = true;
        this.reset();
      } else window.location.reload();
    } catch (err) {
      if (err.error.requireElevation) {
        this.elevateLoanError += `- ⚠️ ${err.error.error}`;
        this.postLoanRequest(loanRequest, true);
      }
    }
  }

  async postSharedLoanRequest(mainRequest: LoanRequest, sharedRequests: LoanRequest[], elevate: boolean) {
    const requestRemark = [mainRequest.Remark, ...sharedRequests.map(r => r.Remark)].filter(e => e.length > 0).join(' ').trim();
    const remark = (elevate == true) ? requestRemark + "=\n" + this.elevateLoanError : requestRemark;
    mainRequest.Remark = remark;
    const sharedLoanRequest = new SharedLoanRequest();
    sharedLoanRequest.SharedProfileId = this.selectedProfile.ID;
    sharedLoanRequest.Guarantors = this.contactList.filter(c => c.IsSelected);
    sharedLoanRequest.LoanRequestsInfo = [mainRequest, ...sharedRequests.map(r => { r.Remark = remark; return r })];

    try {
      const res = await this.loanRequestService.postSharedLoanRequest(sharedLoanRequest, this.selectedFormJ, elevate);
      if (this.sharedService.isStaff() === false) this.agreeShareLoanAgreement(res.LoanGroupId);
      else window.location.reload();
    } catch (err) {
      if (err.error.requireElevation) {
        this.elevateLoanError += `- ⚠️ ${err.error.error}`;
        this.postSharedLoanRequest(mainRequest, sharedRequests, true);
      }
    }
  }

  async agreeShareLoanAgreement(loanGroupId: number) {
    const r = await this.loanRequestService.postProcessSharedLoanAgreement(loanGroupId, 'agree');
    if (r) {
      this.activeModal.close();
      //after agree, redirect to dashboard
      this.router.navigate(['/dashboard-calendar']);
    }
  }

  /*
    * Not using anymore as `elevateSuggestedSettlement` is undefined in all EPFLimit
    checkIfRequireElevate(): boolean {
      const epfCategory = this.sharedService.checkEPFLimitCategory(this.updatingEPF.Date, this.updatingEPF.Amount);

      //calculate settlement of this newly created loan request
      const currentLoanSettlement = this.sharedService.calculateEPFSettlementAmountWithCharges(this.combinedLoan);

      // EPF set Limit
      //if previous + new loan request settlement ratio overlimit
      if (this.enums.epfLimitList[epfCategory].ElevatedSettlementRate != undefined && this.sharedService.calculatePreviousEPFLoanSettlement(this.previousEPFLoans) + currentLoanSettlement > this.sharedService.getEPFElevateSettlement(this.updatingEPF.Date, this.updatingEPF.Amount)) {
        this.elevateSuggestedSettlement += "\n\n- The loan requested has the settlement ratio more than " + this.enums.epfLimitList[epfCategory].ElevatedSettlementRate * 100 + "%. This case will be sent to BOSS FOR APPROVAL"
        return true;
      } else {
        return (this.isManualElevate === true);
      }
    }
  */

  confirmBeforeElevatingCase(): Promise<boolean> {
    const modalRef = this.sharedService.openModal(ElevateConfirmationComponent);
    modalRef.componentInstance.message = this.elevateLoanError;
    return modalRef.result;
  }

  async updateCustomerDetails() {
    this.selectedCustomer = await this.customerService.putCustomer(this.selectedCustomer.ID, this.updatingCustomer.toUpdate());
  }

  createNewRequestObject() {
    const request = { companyId: undefined, companyName: undefined, request: undefined }
    this.requestList.push(request)
  }

  combineAllLoans() {
    const loan: Loan = Object.assign(new Loan(), this.mainLoanRequest.request.LoanInfo);
    loan.Extras = Object.assign([], this.mainLoanRequest.request.LoanInfo.Extras);
    loan.SettlementCharges = Object.assign(new SettlementCharges(), this.mainLoanRequest.request.LoanInfo.SettlementCharges);
    let loanSettlementWithoutCharge = this.sharedService.calculateEPFSettlementAmount(loan.Principal, loan.InterestRate, loan.Term);

    return this.requestList.reduce((result, addedRequest) => {
      const l = addedRequest.request.LoanInfo;
      result.Principal += l.Principal;
      result.Balance += l.Balance;
      result.InterestCharge += l.InterestCharge;
      result.Extras.push(l.Extras[0]);
      result.ServiceCharge += l.ServiceCharge;
      result.StampAmount += l.StampAmount;

      loanSettlementWithoutCharge += this.sharedService.calculateEPFSettlementAmount(l.Principal, l.InterestRate, l.Term);
      const flexiMonthlyAmount = loanSettlementWithoutCharge / result.Term;
      result.InterestRate = this.sharedService.calculateInterestRate(flexiMonthlyAmount, result.Principal, result.Term);

      result.SettlementCharges.Stamp += l.SettlementCharges.Stamp;
      result.SettlementCharges.ServiceCharge += l.SettlementCharges.ServiceCharge;
      result.SettlementCharges.Extras += l.SettlementCharges.Extras;
      result.SettlementCharges.HandlingCharge = (result.SettlementCharges.HandlingCharge ?? 0) + (l.SettlementCharges.HandlingCharge ?? 0);
      // result.SettlementCharges.HandlingCharge += l.SettlementCharges.HandlingCharge;
      result.SettlementExtra += l.SettlementExtra;
      result.SettlementStamp += l.SettlementStamp;
      result.SettlementServiceCharge += l.SettlementServiceCharge;
      return result;
    }, loan);
  }

  validateLoanLimit(loan: Loan): string[] {
    const errors: string[] = [];
    const s = loan.SettlementCharges;

    if (s !== undefined) {
      const totalSettlementCharges = s.Stamp + s.ServiceCharge + s.Extras;

      // Settlement charges must not less than minimum charges per term
      const minSettlement = this.enums.MIN_SSC_PER_TERM * loan.Term;
      if (totalSettlementCharges < minSettlement)
        errors.push(ERROR_MESSAGES.MINIMUM_CURRENCY('Settlement charges', minSettlement));

      // #region Check Settlement Charges Amount
      const firstLoanMaximumSettlementCharges = new SettlementCharges(this.enums.MINIMUM_SC * loan.Term, this.enums.MINIMUM_STAMP * loan.Term, 0);

      // get extra distribution based on total charges collected (sum of all the charges manual key in)
      const extraSupposedDistribution = this.sharedService.calculateAndReturnSettlementCharges(loan.Principal, totalSettlementCharges, loan.Term);

      // * get permitted settlement charges amount
      const p = (this.isEPFExtra == false) ? firstLoanMaximumSettlementCharges : extraSupposedDistribution;

      // check whether settlement stamp = permitted stamp
      if (s.Stamp != p.Stamp)
        errors.push(LOAN_ERROR_MESSAGES.NOT_EQUAL_PERMITTED_SETTLEMENT_CHARGES('STAMP', s.Stamp, p.Stamp, loan.Term));

      // check whether settlement sc  = permitted sc
      if (s.ServiceCharge != p.ServiceCharge)
        errors.push(LOAN_ERROR_MESSAGES.NOT_EQUAL_PERMITTED_SETTLEMENT_CHARGES('SERVICE CHARGE', s.ServiceCharge, p.ServiceCharge, loan.Term));
      // #endregion

      const epfCategory = this.sharedService.checkEPFLimitCategory(this.updatingEPF.Date, this.updatingEPF.Amount ?? 0);
      const EPFLimit = this.enums.epfLimitList[epfCategory];

      if (this.isEPFExtra !== true) {
        // CASH ON HAND MUST BE MULTIPLE OF 100 (EPF FIRST LOAN)
        const cashOnHand = loan.Principal - loan.InterestCharge - loan.StampAmount - loan.ServiceCharge - this.sharedService.calculateDuitLebihAmount(loan.Extras);
        if (cashOnHand % 100 != 0)
          errors.push(LOAN_ERROR_MESSAGES.MULTIPLE_OF('CASH ON HAND', 100));

        // EPF first loan Limit
        const firstLoanLimitAmount = this.sharedService.getEPFLimitFirstLoan(this.updatingEPF.Date, this.updatingEPF.Amount ?? 0);
        if (EPFLimit.FirstLoanRate != undefined && this.isEPFExtra == false && loan.Principal > firstLoanLimitAmount)
          errors.push(LOAN_ERROR_MESSAGES.FIRST_LOAN_LIMIT(EPFLimit.LowerLimit, EPFLimit.UpperLimit, EPFLimit.FirstLoanRate * 100, firstLoanLimitAmount));
      } else {
        /**
         * * EPF overall loan Limit
         * Check EXTRA EPF Loan only (NEW EPF loan is controlled by first loan limit)
         */
        const previousLoanPrincipal = Number(this.sharedService.calculatePreviousEPFLoanPrincipal(this.previousEPFLoans)); // ? why need Number()
        const totalPrincipal = previousLoanPrincipal + loan.Principal;
        const limitPrincipal = this.sharedService.getEPFLimitPrincipal(this.updatingEPF.Date, this.updatingEPF.Amount ?? 0);

        if (EPFLimit.OverallLoanRate !== undefined && totalPrincipal > limitPrincipal)
          errors.push(LOAN_ERROR_MESSAGES.EXTRA_LOAN_LIMIT(EPFLimit.LowerLimit, EPFLimit.UpperLimit, EPFLimit.OverallLoanRate * 100, limitPrincipal, previousLoanPrincipal, loan.Principal));
      }

      // PRINCIPAL MUST BE MULTIPLE OF 50
      if (loan.Principal % 50 != 0)
        errors.push(LOAN_ERROR_MESSAGES.MULTIPLE_OF('PRINCIPAL', 50));

      // STAMP LIMIT - Stamp amount must be MINIMUM STAMP FOR EPF SCHEME WHEN OUT LOAN
      if ((loan.StampAmount == undefined || loan.StampAmount != this.enums.MINIMUM_STAMP))
        errors.push(LOAN_ERROR_MESSAGES.FIXED_VALUE_CURRENCY('Stamp', this.enums.MINIMUM_STAMP));

      // SC LIMIT - Service charge must be MINIMUM SC FOR EPF SCHEME WHEN OUT LOAN
      if ((loan.ServiceCharge == undefined || loan.ServiceCharge != this.enums.MINIMUM_SC))
        errors.push(LOAN_ERROR_MESSAGES.FIXED_VALUE_CURRENCY('Service Charge', this.enums.MINIMUM_SC));

      // EPF Settlement Limit
      const currentLoanSettlement = this.sharedService.calculateEPFSettlementAmountWithCharges(loan);
      const previousSettlement = Number(this.sharedService.calculatePreviousEPFLoanSettlement(this.previousEPFLoans));
      const totalUserSettlement = previousSettlement + currentLoanSettlement;
      const limitSettlement = this.sharedService.getEPFLimitSettlement(this.updatingEPF.Date, this.updatingEPF.Amount ?? 0);
      if (EPFLimit.MaximumSettlementRate != undefined && totalUserSettlement > limitSettlement)
        errors.push(LOAN_ERROR_MESSAGES.SETTLEMENT_LIMIT(EPFLimit.LowerLimit, EPFLimit.UpperLimit, EPFLimit.MaximumSettlementRate * 100, limitSettlement, previousSettlement, currentLoanSettlement));

      //WARN MANUAL KEY IN - FIRST LOAN (not shared)
      if (this.isShared === false && this.isEPFExtra === false && this.isManualKeyIn === true) {
        errors.push(LOAN_ERROR_MESSAGES.MANUAL_KEY_IN);
      }
    }
    return errors;
  }

  validateLoan(loan: Loan): string[] {
    const errors: string[] = [];
    const s = loan.SettlementCharges;

    //SETTLEMENT CHARGES CHECK
    if (s == undefined)
      errors.push(ERROR_MESSAGES.EMPTY_FIELD('Settlement Charges'));
    else {
      //if any of the amount is negative
      if (s.Stamp < 0)
        errors.push(ERROR_MESSAGES.NEGATIVE_NUMBER('Settlement Stamp'));

      if (s.ServiceCharge < 0)
        errors.push(ERROR_MESSAGES.NEGATIVE_NUMBER('Settlement Service Charge'));

      if (s.Extras < 0)
        errors.push(ERROR_MESSAGES.NEGATIVE_NUMBER('Settlement Duit Lebih'));

      if (this.isEPFExtra === true) {
        //check whether handling charge = handling rate*principal
        if (s.HandlingCharge == undefined) {
          errors.push('🚨 ' + ERROR_MESSAGES.EMPTY_FIELD('Handling Charge'));
        } else if (s.HandlingCharge != Math.floor(loan.Principal * this.enums.MININUM_HANDLING_RATE * loan.Term)) {
          errors.push("🚨 Handling Charge of RM" + s.HandlingCharge + " is not equal to 1% of principal x " + loan.Term + "! (RM " + Math.floor(loan.Principal * this.enums.MININUM_HANDLING_RATE * loan.Term) + ")");
        }
      }
    }

    // Form J validation
    const formJ = this.formJList.find(f => f.Id === this.selectedFormJ);
    const totalOnHand = this.sharedService.calculateTotalOnHand(loan);
    if (totalOnHand !== formJ.Principal) {
      errors.push(ERROR_MESSAGES.NOT_MATCH(`Form J Principal (RM${formJ.Principal})`, `Total On Hand (RM${totalOnHand})`));
    }
    if (loan.Term !== formJ.Term) {
      errors.push(ERROR_MESSAGES.NOT_MATCH(`Form J Term (${formJ.Term} months)`, `Loan Term (${loan.Term} months)`));
    }

    return errors;
  }

  async elevateConfirmation() {
    if (this.isManualElevate === true) {
      const modalRef = this.sharedService.openModal(ElevateConfirmationComponent, "regularModal");
      this.isManualElevate = await modalRef.result;
    }
    this.isEPFValid(false, false);
  }

  async manualKeyInConfirmation() {
    if (this.isManualKeyIn == true) {
      const modalRef = this.sharedService.openModal(ElevateConfirmationComponent, "regularModal");
      modalRef.componentInstance.message = "WARNING! You decided to use manual key in instead of system calculated value. This might increase risk of case denial!";
      this.isManualKeyIn = await modalRef.result;
    }
  }

  deleteNewLoan(index: number) {
    if (this.mainLoanRequest != undefined) {
      this.requestList.splice(index, 1);
      this.cdr.detectChanges();
    } else {
      this.sharedService.openAlert("Main request missing!", ALERT_TYPE.DANGER);
    }
  }

  deleteMainLoan() {
    if (this.requestList.length == 0) {
      this.mainLoanRequest = undefined;
      this.cdr.detectChanges();
    } else {
      this.sharedService.openAlert("Cannot delete main loan when there's request!", ALERT_TYPE.DANGER);
    }
  }

  openAddBankAccountModal(contact: Contact) {
    const modalRef = this.sharedService.openModal(BasicDialogComponent);
    const i = modalRef.componentInstance as BasicDialogComponent;
    i.title = 'Add Guarantor Bank Account';
    i.injectContent = [new DialogItem(AddCustomerBankAccountComponent, { customer: this.selectedCustomer, selectedContactId: contact.ID, lockIsContact: true })];
    modalRef.result.then((result) => {
      if (result != undefined)
        contact.AccountExisted = true;
    })
  }

  formatErrors(errors: string[], emptyLine = false): string {
    const e = errors.filter(e => e.trim().length > 0);
    if (e.length > 0)
      return e.map(s => `- ${s}`).join(emptyLine ? '\n\n' : '\n');
  }

  showErrors(error: string) {
    this.sharedService.openAlert(ERROR_MESSAGES.INVALID_FIELDS + "\n" + error, ALERT_TYPE.DANGER);
  }
}
